// FILE: src/components/AdminGate.js
//
// Lightweight authentication gate for the Admin overlay.
// Wraps all admin screens — if the user isn't signed in via Google / Microsoft
// / Email magic link, shows a sign-in prompt instead of the admin content.
//
// ── Auth flow ────────────────────────────────────────────────────────────────
//
//   1. User triggers admin (button or Ctrl+`)
//   2. AdminGate checks for an active Supabase auth session
//   3. If no session → show sign-in card (Google / Microsoft / Email magic link)
//   4. On sign-in → Supabase redirects to the app, session is established
//   5. AdminGate checks email against the admin allow-list (see below)
//   6. If allowed → render children (admin screens)
//   7. If not allowed → show "access denied" message
//
// ── Admin allow-list (Phase 6) ───────────────────────────────────────────────
//
//   The allow-list is the UNION of two sources:
//
//     1. The `admin_users` Postgres table (managed via the new Admin Users
//        screen).  This is the day-to-day source of truth — admins are added
//        and removed from the UI without redeploying.
//
//     2. The `REACT_APP_BOOTSTRAP_ADMINS` env var (comma-separated emails),
//        kept as an emergency-unlock fallback so the project owner cannot
//        accidentally lock themselves out.  `REACT_APP_ADMIN_EMAILS` is
//        recognised as a legacy alias for this same role.
//
//   The migration seeds the bootstrap admins into the DB so the table is
//   never empty.  If the DB is unreachable, env-var bootstrap admins still
//   get in (read-only — admin management requires the DB to be reachable).
//
// ── E2E auth bypass ─────────────────────────────────────────────────────────
//
// When REACT_APP_E2E_BYPASS_AUTH is 'true', AdminGate skips all Supabase
// authentication and renders children immediately.  Used ONLY for Playwright
// CI / controlled E2E runs.  MUST NEVER be enabled in production builds.

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../services/supabaseClient';
import { listAdminUsers } from '../../services/adminUserService';

const E2E_BYPASS_AUTH = process.env.REACT_APP_E2E_BYPASS_AUTH === 'true';

// ── Parse env-var bootstrap admins ───────────────────────────────────────────
//
// REACT_APP_BOOTSTRAP_ADMINS is the canonical name as of Phase 6.
// REACT_APP_ADMIN_EMAILS is a legacy alias kept for backwards-compat during
// the transition window.

function getBootstrapAdmins() {
  return (
    (process.env.REACT_APP_BOOTSTRAP_ADMINS || '') + ',' +
    (process.env.REACT_APP_ADMIN_EMAILS    || '')
  )
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  wrapper: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#1a1a2e',
    color: '#fff',
    padding: '16px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '1.15rem',
    fontWeight: 'bold',
    flex: 1,
    letterSpacing: '0.5px',
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 32px',
    textAlign: 'center',
  },
  card: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '48px 40px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  icon: {
    fontSize: '3rem',
    marginBottom: '16px',
    display: 'block',
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#222',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#666',
    marginBottom: '28px',
    lineHeight: '1.5',
  },
  googleButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 28px',
    background: '#fff',
    border: '1px solid #dadce0',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontWeight: '500',
    color: '#3c4043',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s, background 0.15s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  signOutButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  denied: {
    color: '#c62828',
    fontWeight: '600',
    marginBottom: '12px',
  },
  deniedEmail: {
    fontSize: '0.85rem',
    color: '#888',
    marginBottom: '20px',
  },
  loading: {
    fontSize: '1rem',
    color: '#888',
  },
};

// ── Components ──────────────────────────────────────────────────────────────
//
// Two separate components avoid calling hooks conditionally.  The module-
// level constant E2E_BYPASS_AUTH picks which one is exported as default.

/**
 * Bypass variant — renders children immediately with a synthetic context.
 * No Supabase calls are made.  Used only for Playwright CI / E2E runs.
 */
function AdminGateBypass({ onBack, children }) {
  return (
    <AdminGateContext.Provider
      value={{
        session: { user: { email: 'e2e@test.local' } },
        userEmail: 'e2e@test.local',
        isBootstrapAdmin: true,
        dbAdminsAvailable: false,
        refreshAdmins: () => Promise.resolve(),
        onSignOut: onBack,
      }}
    >
      {children}
    </AdminGateContext.Provider>
  );
}

/**
 * Production variant — full Supabase OAuth flow with email allowlist.
 */
function AdminGateAuth({ onBack, children }) {
  const [session, setSession] = useState(undefined); // undefined = loading
  const [error, setError] = useState(null);

  // Admin list from the DB.  null = not yet loaded; 'error' = fetch failed
  // (we still allow env-var bootstrap admins in this case).
  const [dbAdmins, setDbAdmins] = useState(null);

  // ── Listen for auth state changes ────────────────────────────────────────
  useEffect(() => {
    // Track the currently signed-in user so we can ignore benign auth events.
    // Supabase fires onAuthStateChange (TOKEN_REFRESHED / SIGNED_IN) every
    // time the browser tab regains focus, each time handing us a brand-new
    // session object. Calling setSession on those churns AdminGate, which
    // resets dbAdmins to null and unmounts the admin children — wiping an
    // in-progress Question Editor form whenever the user tabs away to look
    // something up. Only update state when the signed-in user actually
    // changes (sign-in, sign-out, or account switch).
    let currentUserId = null;

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      currentUserId = s?.user?.id ?? null;
      setSession(s);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, s) => {
        const nextUserId = s?.user?.id ?? null;
        if (nextUserId === currentUserId) return; // token refresh / refocus — ignore
        currentUserId = nextUserId;
        setSession(s);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // ── Fetch admin_users when a session appears ─────────────────────────────
  const refreshAdmins = useCallback(async () => {
    try {
      const rows = await listAdminUsers();
      setDbAdmins(Array.isArray(rows) ? rows : []);
    } catch (err) {
      // The RPC raises "Not authorized" for non-admin callers, which is the
      // expected response for a denied user.  Treat both that and network
      // failures as 'error' — the env-var fallback handles bootstrap admins.
      // eslint-disable-next-line no-console
      console.warn('[AdminGate] listAdminUsers failed:', err?.message);
      setDbAdmins('error');
    }
  }, []);

  useEffect(() => {
    if (session) {
      setDbAdmins(null);
      refreshAdmins();
    } else {
      setDbAdmins(null);
    }
  }, [session, refreshAdmins]);

  // ── Sign in with Google ──────────────────────────────────────────────────
  const handleSignIn = async () => {
    setError(null);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (err) setError(err.message);
  };

  // ── Sign in with Microsoft (Azure AD / Entra) ───────────────────────────
  const handleSignInMicrosoft = async () => {
    setError(null);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: window.location.origin,
        scopes: 'email',
      },
    });
    if (err) setError(err.message);
  };

  // ── Email magic link (passwordless OTP) ─────────────────────────────────
  const [magicEmail,    setMagicEmail]    = useState('');
  const [magicState,    setMagicState]    = useState('idle');  // idle|sending|sent|error
  const [magicErrorMsg, setMagicErrorMsg] = useState(null);

  const handleSignInMagicLink = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!magicEmail || !magicEmail.trim()) return;
    setMagicState('sending');
    setMagicErrorMsg(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: magicEmail.trim().toLowerCase(),
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    if (err) {
      setMagicState('error');
      setMagicErrorMsg(err.message || 'Could not send magic link.');
      return;
    }
    setMagicState('sent');
  };

  // ── Sign out ─────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onBack();
  };

  // ── Loading state ────────────────────────────────────────────────────────
  if (session === undefined) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={onBack}>← Back</button>
          <span style={styles.headerTitle}>⚙️ Admin</span>
        </div>
        <div style={styles.body}>
          <span style={styles.loading}>Checking authentication…</span>
        </div>
      </div>
    );
  }

  // ── Not signed in → show sign-in card ────────────────────────────────────
  if (!session) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={onBack}>← Back</button>
          <span style={styles.headerTitle}>⚙️ Admin</span>
        </div>
        <div style={styles.body}>
          <div style={styles.card}>
            <span style={styles.icon}>🔒</span>
            <div style={styles.title}>Admin Access</div>
            <div style={styles.subtitle}>
              Sign in to access the admin tools. Your account email must be on the allow-list.
            </div>

            {/* Google ─────────────────────────────────────────────────── */}
            <button
              data-testid="admin-google-signin"
              style={styles.googleButton}
              onClick={handleSignIn}
              onMouseOver={(e) => { e.currentTarget.style.background = '#f8f9fa'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.1 24.1 0 0 0 0 21.56l7.98-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>

            {/* Microsoft (Azure AD / Entra) ───────────────────────────── */}
            <button
              data-testid="admin-microsoft-signin"
              style={{ ...styles.googleButton, marginTop: '10px' }}
              onClick={handleSignInMicrosoft}
              onMouseOver={(e) => { e.currentTarget.style.background = '#f8f9fa'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; }}
            >
              <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                <rect x="1"  y="1"  width="10" height="10" fill="#f25022"/>
                <rect x="12" y="1"  width="10" height="10" fill="#7fba00"/>
                <rect x="1"  y="12" width="10" height="10" fill="#00a4ef"/>
                <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </button>

            {/* Email magic link ──────────────────────────────────────── */}
            <div style={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid #eee',
              textAlign: 'left',
            }}>
              <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: '6px' }}>
                Or get a one-time email link:
              </div>
              {magicState !== 'sent' ? (
                <form onSubmit={handleSignInMagicLink} style={{ display: 'flex', gap: '6px' }}>
                  <input
                    data-testid="admin-magic-email-input"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    required
                    disabled={magicState === 'sending'}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      fontSize: '0.85rem',
                      border: '1px solid #dadce0',
                      borderRadius: '4px',
                      outline: 'none',
                    }}
                  />
                  <button
                    data-testid="admin-magic-send"
                    type="submit"
                    disabled={magicState === 'sending' || !magicEmail.trim()}
                    style={{
                      padding: '8px 14px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#fff',
                      background: magicState === 'sending' ? '#90a4ae' : '#1565c0',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: magicState === 'sending' ? 'wait' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {magicState === 'sending' ? 'Sending…' : 'Email me a link'}
                  </button>
                </form>
              ) : (
                <div
                  data-testid="admin-magic-sent"
                  style={{
                    fontSize: '0.85rem',
                    color: '#2e7d32',
                    background: '#f1f8f1',
                    border: '1px solid #c8e6c9',
                    padding: '8px 10px',
                    borderRadius: '4px',
                  }}
                >
                  ✓ Check your email at <strong>{magicEmail}</strong> for a sign-in link.
                </div>
              )}
              {magicErrorMsg && (
                <div data-testid="admin-magic-error" style={{ color: '#c62828', marginTop: '8px', fontSize: '0.85rem' }}>
                  {magicErrorMsg}
                </div>
              )}
            </div>

            {error && (
              <div style={{ color: '#c62828', marginTop: '16px', fontSize: '0.85rem' }}>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Signed in → resolve admin status ─────────────────────────────────────
  const userEmail = session.user?.email?.toLowerCase() || '';

  // Wait for the DB admin list before deciding.  Show a brief "checking
  // permissions" splash so we don't flash "denied" at users whose row is
  // about to load.
  if (dbAdmins === null) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={onBack}>← Back</button>
          <span style={styles.headerTitle}>⚙️ Admin</span>
        </div>
        <div style={styles.body}>
          <span style={styles.loading}>Checking permissions…</span>
        </div>
      </div>
    );
  }

  const dbEmails = Array.isArray(dbAdmins)
    ? dbAdmins.map((a) => (a.email || '').toLowerCase()).filter(Boolean)
    : [];
  const isBootstrapAdmin = getBootstrapAdmins().includes(userEmail);
  const isDbAdmin        = dbEmails.includes(userEmail);
  const isAdmin          = isDbAdmin || isBootstrapAdmin;

  if (!isAdmin) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={onBack}>← Back</button>
          <span style={styles.headerTitle}>⚙️ Admin</span>
          <button style={styles.signOutButton} onClick={handleSignOut}>Sign out</button>
        </div>
        <div style={styles.body}>
          <div style={styles.card}>
            <span style={styles.icon}>🚫</span>
            <div style={styles.denied}>Access Denied</div>
            <div style={styles.deniedEmail}>
              Signed in as <strong>{session.user?.email}</strong>
            </div>
            <div style={styles.subtitle}>
              This account is not authorized to access admin tools.
              Contact the project owner for access.
            </div>
            <button
              style={{ ...styles.googleButton, color: '#c62828', borderColor: '#ef9a9a' }}
              onClick={handleSignOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Authorized — render admin content with auth context ──────────────────
  return (
    <AdminGateContext.Provider
      value={{
        session,
        userEmail,
        isBootstrapAdmin,
        dbAdminsAvailable: dbAdmins !== 'error',
        refreshAdmins,
        onSignOut: handleSignOut,
      }}
    >
      {children}
    </AdminGateContext.Provider>
  );
}

// ── Export the appropriate variant based on build-time flag ──────────────────
//
// Named exports allow tests to verify each variant independently without
// needing jest.isolateModules() (which conflicts with CRA's React setup).

export { AdminGateBypass, AdminGateAuth };
export default E2E_BYPASS_AUTH ? AdminGateBypass : AdminGateAuth;

// ── Context for child components to access auth info ─────────────────────────

export const AdminGateContext = React.createContext(null);
